/* The help registry (designs/help-layer.md HL1) — every explanatory string
   the non-intake app renders, in one reviewed file. ONE deliberate
   exception since AB6: the "Intake" section About is not re-worded here, it
   is SOURCED from src/shared/intake-about-module.ts, so the section's ⓘ and
   the module's own About page cannot drift apart. Intake's copy still lives
   with intake. Each entry paraphrases a
   design doc, cited in its comment: copy cannot drift from the design
   without this file's diff saying so. Renderers never inline help prose.

   Three tiers (HL2/HL3):
   - CONCEPTS  → the load-bearing definitions, rendered as ⓘ disclosure
     panels by concept() — click/keyboard/touch, never hover-only.
   - HELP      → the hover tier: title="" strings for headers, badges,
     labels, and menu verbs.
   - SECTION_ABOUT → the per-section About panels (HL5).
   About Hermes (HL4) composes from all three. */

import { esc } from "./esc";
import { INTAKE_ABOUT_MODULE } from "../shared/intake-about-module";

/* ── the ⓘ tier (HL2): load-bearing, disclosed in place ── */

export interface Concept {
  id: string;
  title: string;
  /* short paragraphs, plain text — concept() escapes and joins them */
  body: readonly string[];
}

export const CONCEPTS = [
  {
    /* project-record-states.md (its Sep 14 2026 note: states no longer move
       anything else), directories.md DIR2 */
    id: "record-states",
    title: "Draft · Review · Active · Archived",
    body: [
      "Every project carries one of four states — a status stamped on the record, not a folder it lives in.",
      "Draft: being shaped. Review: put forward for approval. Active: approved and running. Archived: closed; kept, never deleted.",
      "The File menu drives the walk: Submit for review → Approve (or Back to draft) → Archive. A move changes the state and nothing else, and every move is a stamped revision, so a mistake is undone by moving back.",
    ],
  },
  {
    /* per-month-role-needs.md PN3, fte.ts */
    id: "fte-and-demand",
    title: "FTE and demand",
    body: [
      "Demand is what a project needs: so many FTE (full-time equivalents) of a role, for a dated range of months. 1.0 FTE = one person full-time for that month; 0.5 FTE = half their time.",
      "Every month is its own record. A month with no bar is a month with nothing recorded — not a zero.",
    ],
  },
  {
    /* operational-hardening/settled decisions; charter-and-roster.md; teams-page.md */
    id: "stamps-and-honesty",
    title: "Stamps, red, and orange",
    body: [
      "Every value carries a stamp: the source it came from, who entered it, and when it took effect. Changing a value appends a new revision — history is never overwritten.",
      "Red — “not recorded” — means nobody has entered it. The app never fills a gap with a guess.",
      "Orange — “unconfirmed” — means someone recorded it AS an assumption, and said so.",
    ],
  },
  {
    /* organizer-roles.md, authorization-and-attribution.md, canonical-roles.md
       CR8 — checked against the server's gates: asAdmin, asRecordKeeper,
       asCatalogAuthority (Admin only) and asIntakeAdmin */
    id: "who-can-do-what",
    title: "Who can do what",
    body: [
      "Admin (granted on the Permissions page): grants roles, deletes projects, adds and corrects roles in the catalog, grooms intake efforts and records their outcomes. Record Keeper (granted): creates and edits projects.",
      "Dev Manager is DERIVED, not granted — whoever the org chart names as a team's manager holds it, and it changes in Entra, not here. Nothing in Hermes is gated on it today.",
      "Reads are open to every signed-in person; every change is stamped with who made it.",
    ],
  },
  {
    /* directories.md DIR1–DIR7 */
    id: "directories",
    title: "Folders and filing",
    body: [
      "Directories are folders the team creates and names — Mining, Ag, Haul Trucks — per section, nested as deep as needed. A record files into exactly one, or shows under “Not filed”.",
      "Filing says where a record belongs; the draft/review/active/archived badge says what state it is in. The two are independent — approving a record never moves it.",
    ],
  },
] as const satisfies readonly Concept[];

export type ConceptId = (typeof CONCEPTS)[number]["id"];

const conceptById = (id: ConceptId): Concept => CONCEPTS.find((c) => c.id === id)!;

/* the ⓘ disclosure (HL2): native details/summary — keyboard and touch for
   free, no popover machinery. openAbout is wired by app.ts so every panel
   can hand off to the full story. */
export function concept(id: ConceptId): string {
  const c = conceptById(id);
  return `<details class="concept">
    <summary aria-label="What is ${esc(c.title)}?"><span class="concept-i">ⓘ</span> ${esc(c.title)}</summary>
    <div class="concept-body">
      ${c.body.map((p) => `<p>${esc(p)}</p>`).join("")}
      <button class="concept-more" data-view="about-hermes">More in About Hermes →</button>
    </div>
  </details>`;
}

/* the colour key (HL3) — one line wherever the badges render, replacing
   the four lucky footnotes the audit found */
export const badgeKey = (): string =>
  `<p class="badge-key"><span class="state missing">not recorded</span> nobody has entered it ·
   <span class="state unconfirmed">unconfirmed</span> recorded as an assumption</p>`;

/* ── the hover tier (HL3) ── */

export const HELP: Record<string, string> = {
  /* demand months — per-month-role-needs.md */
  "col-month": "Each month is its own record — a dash is “nothing recorded”, never zero.",
  /* record states / File verbs — project-record-states.md: a move changes
     the state and nothing else (Sep 14 2026) */
  "op-submit": "Draft → Review: puts the project forward for approval.",
  "op-approve": "Review → Active: approves the project. Only its state changes; the move is a stamped revision.",
  "op-demote": "Back to draft: returns the project to draft. Only its state changes; nothing is lost.",
  /* the verbs as the File menu actually spells them — keyed by first word */
  "op-back": "Back to draft: returns the project to draft. Only its state changes; nothing is lost.",
  "op-unarchive": "Reopens the record where it left off — nothing was lost while it was archived.",
  "op-new": "Creates a draft record. A draft is visible and editable.",
  "op-view": "Opens the picker — folders first, and a search that narrows as you type.",
  "op-edit": "Edits the record's fields. Every change appends a stamped revision; history is never overwritten.",
  "op-close": "Closes this record's tabs and returns to the list — the record itself is untouched.",
  "op-archive": "Closes the record. Kept with its whole history; unarchive brings it back.",
  "op-delete": "Admin-only. Removes the record AND its history — the one operation that forgets.",
  /* stamps */
  stamp: "Source · who entered it · when it took effect. Values append revisions; history is never overwritten.",
  "state-badge": "The record's status — draft, review, active, or archived. A status on the record, not a folder it lives in.",
  /* resourcing — per-month-role-needs.md */
  "demand-chart": "Demand — what the project needs, in FTE — month by month.",
  "demand-entry": "A dated need: from month, optional to month (leave empty for open-ended — never invent an end), and how many FTE.",
  /* roles — canonical-roles.md */
  "role-cardinality": "How many people may hold this role on one project. Tightening it later flags conflicting projects, never rewrites them.",
  "role-retire": "Retired roles leave the pickers but keep their history and stay wherever already recorded.",
  /* permissions — organizer-roles.md */
  "grant-derived": "Derived from the team roster — whoever Entra names as manager holds it. It cannot be granted or revoked here.",
  "grant-bootstrap": "Held via the ADMIN_USERS environment — the bootstrap that makes the first grant possible. Not revocable from the app.",
  /* directories — directories.md */
  "not-filed": "No folder recorded — an honest gap, not a default. File it from the record's Edit form.",
};

/* attribute-ready: help("stamp") → ` title="…"` (escaped), "" for unknown
   ids so a renderer can never crash on a missing entry */
export const help = (id: string): string => {
  const text = HELP[id];
  return text === undefined ? "" : ` title="${esc(text)}"`;
};

/* ── the per-section About panels (HL5) ── */

export interface SectionAbout {
  /* keyed by the drawer label */
  section: string;
  what: string;
  why: string;
  how: string;
}

export const SECTION_ABOUT: readonly SectionAbout[] = [
  {
    /* intake-about.md AB6. Intake was the only nav section with NO entry
       here, so aboutThisPage("Intake") answered with an empty string and
       its ⓘ disclosed nothing (found by the Sep-6 help audit). Sourced from
       the intake registry rather than restated — one story, two lengths. */
    section: "Intake",
    what: INTAKE_ABOUT_MODULE.shortWhat,
    why: INTAKE_ABOUT_MODULE.shortWhy,
    how: INTAKE_ABOUT_MODULE.shortHow,
  },
  {
    /* products-pdp.md — the Products section over the PDP (the portfolio
       version of this entry went with the Sep 14 MVP cut) */
    section: "Products",
    what: "Every product — a PDP track owned by a team (a team may own several; platform teams own platform products such as PRAK) — with its market and vehicles, measured against the PDP: which phase it declares, effective-dated, and what that phase owes from the catalog.",
    why: "The owed deliverables, exit criteria and the gate ahead all follow from one declaration — so the org can see where each product stands without asking around.",
    how: "Each product belongs to a team — product, other or platform — and a team may own several, each its own PDP with its own name, market and vehicles. Open a product and use Change phase… to declare a phase (0–6) or “not in the PDP” with a reason; withdraw from the ⋯ menu. Every declaration is stamped and the history is kept. A team's first product is made when it gets the product or other type on Teams; an Admin adds more with New product…, and renames, moves, retires or sets the market and vehicles of one with Edit product….",
  },
  {
    /* projects-page.md, charter-and-roster.md, per-month-role-needs.md */
    section: "Projects",
    what: "The record spine: every project's charter (its span), its role demand in FTE by month (Resourcing), its roles, and its charge code.",
    why: "The living record that replaces the Word charter — and where an intake effort lands when its outcome is a project.",
    how: "Records walk draft → review → active → archived; each move changes the state and nothing else, and is a stamped revision. Demand is recorded per dated month; people come from the Org chart.",
  },
  {
    /* teams-page.md — shown as Org since teams-and-products.md TP8 */
    section: "Org",
    what: "The org chart, mirrored from Entra: each manager's team and the people in it.",
    why: "Hermes never edits the org chart — Entra owns it. This is where Hermes finds people: the point of contact on an effort, a platform reviewer, who to contact about a problem, and who holds which role.",
    how: "Open a team to see its people. Not the same as Teams: Org is who reports to whom; Teams is the list of teams that register and own intake efforts.",
  },
  {
    /* teams-and-products.md TP8 */
    section: "Teams",
    what: "The one list of teams intake speaks — platform teams, product teams (one per PDP) and other teams (customer programs and prototype work). Admins only.",
    why: "Anyone can add a team from the intake form, so the list needs curating: a team added there waits here for an Admin to say its type, or to merge it into the team it duplicates.",
    how: "Rename a team in place; set its type; Mark reviewed or merge an addition; retire a team to hide it from new efforts. Giving a team the product or other type makes its first product; its products — each a PDP track with its own market and vehicles — are managed on Products. Every change is a new revision.",
  },
  {
    /* canonical-roles.md */
    section: "Roles",
    what: "The one canonical list of role names every project describes its needs from.",
    why: "Free-text roles fork the vocabulary — “SDET” and “Test Engineer” stop matching. One list keeps demand, rosters, and reports speaking the same words.",
    how: "Anyone signed in can read; adding or correcting a role needs an Admin. Near-matches warn before a duplicate is born; retiring keeps history.",
  },
  {
    /* organizer-roles.md */
    section: "Permissions",
    what: "Who holds which organizer role — granted Admin and Record Keeper, derived Dev Manager — and what each gates.",
    why: "Mutations need accountable authority; the grants are records like everything else, stamped with who granted them and when.",
    how: "Grant or revoke from a user's card. Derived roles change in Entra, not here; the bootstrap Admin comes from the environment and cannot be revoked in-app.",
  },
];

export const sectionAbout = (label: string): SectionAbout | undefined =>
  SECTION_ABOUT.find((s) => s.section === label);

/* the nav-bar affordance (HL5) */
export function aboutThisPage(label: string): string {
  const a = sectionAbout(label);
  if (a === undefined) return "";
  return `<details class="concept section-about">
    <summary aria-label="About ${esc(label)}"><span class="concept-i">ⓘ</span> About this page</summary>
    <div class="concept-body">
      <p><b>What it is.</b> ${esc(a.what)}</p>
      <p><b>Why it exists.</b> ${esc(a.why)}</p>
      <p><b>How it works.</b> ${esc(a.how)}</p>
      <button class="concept-more" data-view="about-hermes">More in About Hermes →</button>
    </div>
  </details>`;
}
